/* eslint-disable camelcase */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import jwt_decode from 'jwt-decode';
import { EmptyUser, User, QuirkCommunity } from 'model';
import { fetchLike as fetch } from '../api/baseQueryWithReauth.js';
import { getPreference, setPreference } from '../hooks/usePreferences.js';
import { getDevice } from '../hooks/useDevice.js';
import { Preferences } from '@capacitor/preferences';
import { getBackendDomain } from '../hooks/useBackendDomain.js';
import { HttpRequest } from '../App.js';

export interface LoginState {
	loggedIn: 'invalid' | 'valid' | 'validating' | null;
	user: User;
	emailLogin: 'sending' | 'failed' | 'sent' | null;
	newPassword: string;
	confirmNewPassword: string;
	loginMessage: string;
	loginError: string;
	requireTermsOfService: boolean;
	passwordResetErrorMessage: string;
	passwordResetStatus: 'reset' | 'resetting' | 'error' | 'setting' | 'set' | null;
	allowAnonymous: boolean;
	domain: string;
	imageToken: string;
	platform: 'web' | 'ios' | 'android' | null;
	communities: QuirkCommunity[] | null;
}

const initialState: LoginState = {
	loggedIn: 'valid',
	user: EmptyUser,
	loginMessage: '',
	emailLogin: null,
	loginError: '',
	passwordResetErrorMessage: '',
	newPassword: '',
	confirmNewPassword: '',
	passwordResetStatus: null,
	requireTermsOfService: false,
	domain: '',
	imageToken: '',
	platform: null,
	allowAnonymous: false,
	communities: null
};

const REFRESH_CANCEL = 'REFRESH_CANCEL';

async function getAllowAnonymous() {
	const siteSettings = await fetch('/api/siteSettings/info', {
		method: 'GET',
		cache: 'no-cache',
		headers: {
			'Content-Type': 'application/json'
		}
	});
	let allowAnonymous = false;
	if (siteSettings.status === 200) {
		const settings = await siteSettings.json();
		allowAnonymous = settings.allowAnonymous;
	}
	return allowAnonymous;
}

export const logoutFetch = async (domain: string | null) => {
	// This will just kill the user's jwt cookies
	await fetch('/api/users/logout/', {
		method: 'GET',
		cache: 'no-cache',
		headers: {
			'Content-Type': 'application/json'
		}
	});
	await Preferences.remove({ key: domain + '_jwt_token' });
	await Preferences.remove({ key: domain + '_jwt_image_token' });
	await Preferences.remove({ key: domain + '_jwt_refresh_token' });
};

export const refreshFetch = async (): Promise<{
	token: string;
	refreshToken: string;
	requireTermsOfService: boolean;
}> => {
	const response = await fetch('/api/users/refreshToken/', {
		method: 'GET',
		cache: 'no-cache',
		headers: {
			'Content-Type': 'application/json'
		}
	});
	if (response.status === 200) {
		const payload = await response.json();
		const decoded = jwt_decode(payload.token);
		const user = (decoded as any).user as User;
		if (user.email.startsWith('anon-')) {
			const allowAnonymous = await getAllowAnonymous();
			if (!allowAnonymous) {
				throw new Error('Anonymous users not allowed');
			}
		}
		return payload;
	} else if (response.status === 400) {
		throw new Error(await response.json());
	} else if (response.status === 401) {
		// Check to see if we allow anonymous users, if not, throw an error
		const allowAnonymous = await getAllowAnonymous();

		if (!allowAnonymous) {
			throw new Error(await response.json());
		}
		// Try for anonymous auth
		const responseAnon = await fetch('/api/users/anonymousAuth/', {
			method: 'GET',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json'
			}
		});
		if (responseAnon.status === 200) {
			return await responseAnon.json();
		} else {
			throw new Error(await responseAnon.json());
		}
	} else {
		// Some sort of intermittent error
		throw new Error(REFRESH_CANCEL);
	}
};
export const refreshToken = createAsyncThunk('login/refresh', refreshFetch);
export const fetchDomain = createAsyncThunk('login/fetchDomain', async (): Promise<string | null> => {
	const domain = await getPreference('backend-domain');
	return domain;
});

export const fetchCommunities = createAsyncThunk(
	'login/fetchCommunities',
	async (arg: void | undefined, api): Promise<QuirkCommunity[]> => {
		const response = await HttpRequest({ url: 'https://api.quirklocal.com/api/sites.json', method: 'GET' });
		if (response.status === 200) {
			const communities: QuirkCommunity[] = response.data;
			if (communities.length === 1) {
				await setPreference('backend-domain', communities[0].hostname);
				api.dispatch(setDomain(communities[0].hostname));
			}
			return communities;
		}
		throw new Error(`Invalid status: ${response.status} received`);
	}
);

export const fetchImageToken = createAsyncThunk('login/fetchImageToken', async (): Promise<string | null> => {
	const domain = await getPreference('backend-domain');
	const imageToken = await getPreference(domain + '_jwt_image_token');
	return imageToken;
});

export const fetchPlatform = createAsyncThunk('login/fetchPlatform', async (): Promise<LoginState['platform']> => {
	const platform = (await getDevice())?.platform;
	return platform;
});

export const logout = createAsyncThunk('login/logout', async () => {
	await logoutFetch(await getBackendDomain());
	// Redirect to root, and authorization will fail and redirect to login page
	window.location.href = '/';
});

export const resetPassword = createAsyncThunk(
	'login/resetPassword',
	async ({ email }: { email: string }): Promise<{ email: string; username: string }> => {
		const response = await fetch('/api/users/resetPassword/' + email, {
			method: 'GET',
			cache: 'no-cache'
		});
		if (response.status === 200) {
			return await response.json();
		} else {
			throw new Error(await response.json());
		}
	}
);

export const requestEmailLogin = createAsyncThunk(
	'login/requestEmailLogin',
	async ({ email }: { email: string }): Promise<{ email: string }> => {
		const response = await fetch('/api/users/requestEmailLogin/' + encodeURIComponent(email), {
			method: 'GET',
			cache: 'no-cache'
		});
		if (response.status === 200) {
			return await response.json();
		} else {
			throw new Error(await response.json());
		}
	}
);

export const executeEmailLogin = createAsyncThunk('login/executeEmailLogin', async ({ token }: { token: string }) => {
	const response = await fetch('/api/users/executeEmailLogin/', {
		method: 'POST',
		cache: 'no-cache',
		headers: {
			'Content-Type': 'application/json',
			'Email-Login-Token': token
		},

		body: JSON.stringify({ token })
	});
	if (response.status === 200) {
		return await response.json();
	} else {
		throw new Error(await response.json());
	}
});

export const requestNewPassword = createAsyncThunk(
	'login/setPassword',
	async ({
		newPassword,
		token
	}: {
		newPassword: string;
		token: string;
	}): Promise<{ token: string; refreshToken: string }> => {
		const response = await fetch('/api/users/setNewPassword/', {
			method: 'POST',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json',
				'Password-Reset-Token': token
			},

			body: JSON.stringify({ newPassword, token })
		});
		if (response.status === 200) {
			return await response.json();
		} else {
			throw new Error(await response.json());
		}
	}
);

export const authenticate = createAsyncThunk(
	'login/authenticate',
	async ({
		email,
		password
	}: {
		email: string;
		password: string;
	}): Promise<{ token: string; refreshToken: string; requireTermsOfService: boolean }> => {
		const response = await fetch('/api/users/authenticate', {
			method: 'POST',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ email, password })
		});
		return await response.json();
	}
);

export const loginSlice = createSlice({
	name: 'login',
	initialState,
	reducers: {
		setEmail: (state, { payload }: { payload: string | null | undefined }) => {
			state.user.email = payload ?? '';
		},
		setPassword: (state, action) => {
			state.user.password = action.payload;
		},
		setLoggedIn: (state, action) => {
			state.loggedIn = action.payload;
		},
		setLoginMessage: (state, action) => {
			state.loginMessage = action.payload;
		},
		setLoginError: (state, action) => {
			state.loginError = action.payload;
		},
		setPasswordResetErrorMessage: (state, action) => {
			state.passwordResetErrorMessage = action.payload;
		},
		setPasswordResetStatus: (state, action) => {
			state.passwordResetStatus = action.payload;
		},
		setNewPassword: (state, action) => {
			state.newPassword = action.payload;
		},
		setConfirmNewPassword: (state, action) => {
			state.confirmNewPassword = action.payload;
		},
		setRequireTermsOfService: (state, action) => {
			state.requireTermsOfService = action.payload;
		},
		setEmailLogin: (state, action: { payload: LoginState['emailLogin'] }) => {
			state.emailLogin = action.payload;
		},
		setDomain: (state, action: { payload: LoginState['domain'] }) => {
			state.domain = action.payload;
		},
		setImageToken: (state, action: { payload: LoginState['imageToken'] }) => {
			state.imageToken = action.payload;
		},
		setPlatform: (state, action: { payload: LoginState['platform'] }) => {
			state.platform = action.payload;
		}
	},
	extraReducers: builder => {
		// Add reducers for additional action types here, and handle loading state as needed
		builder.addCase(authenticate.fulfilled, (state, action) => {
			// Add user to the state array
			try {
				const decoded = jwt_decode(action.payload.token);
				state.user = (decoded as any).user as User;
				state.loggedIn = 'valid';
				state.loginMessage = 'Successfully logged in';
				state.requireTermsOfService = action.payload.requireTermsOfService;
			} catch (e) {
				state.loggedIn = 'invalid';
				state.loginError = 'Invalid email or password';
			}
		});
		builder.addCase(authenticate.rejected, state => {
			state.loggedIn = 'invalid';
		});

		builder.addCase(executeEmailLogin.fulfilled, (state, action) => {
			// Add user to the state array
			try {
				const decoded = jwt_decode(action.payload.token);
				state.user = (decoded as any).user as User;
				state.loggedIn = 'valid';
				state.loginMessage = 'Successfully logged in';
				state.requireTermsOfService = action.payload.requireTermsOfService;
			} catch (e) {
				state.loggedIn = 'invalid';
				state.loginError = 'Invalid email login';
			}
		});
		builder.addCase(executeEmailLogin.rejected, state => {
			state.loggedIn = 'invalid';
		});
		builder.addCase(executeEmailLogin.pending, state => {
			// Add user to the state array
			state.loggedIn = 'validating';
		});

		builder.addCase(requestEmailLogin.fulfilled, state => {
			// Add user to the state array
			state.emailLogin = 'sent';
		});
		builder.addCase(requestEmailLogin.rejected, state => {
			state.emailLogin = 'failed';
		});
		builder.addCase(requestEmailLogin.pending, state => {
			// Add user to the state array
			state.emailLogin = 'sending';
		});
		builder.addCase(refreshToken.fulfilled, (state, action) => {
			// Add user to the state array
			try {
				const decoded = jwt_decode(action.payload.token);
				state.user = (decoded as any).user as User;
				state.loggedIn = 'valid';
				state.requireTermsOfService = action.payload.requireTermsOfService;
			} catch (e) {
				state.loggedIn = 'invalid';
			}
		});

		builder.addCase(refreshToken.rejected, (state, action) => {
			if ((action as any)?.error?.message !== REFRESH_CANCEL) {
				state.loggedIn = 'invalid';
			}
		});
		builder.addCase(resetPassword.fulfilled, (state, action) => {
			state.passwordResetStatus = 'reset';
		});
		builder.addCase(resetPassword.pending, (state, action) => {
			state.passwordResetStatus = 'resetting';
		});
		builder.addCase(resetPassword.rejected, (state, action) => {
			state.passwordResetStatus = 'error';
			state.passwordResetErrorMessage = action.error.message ?? '';
		});
		builder.addCase(requestNewPassword.fulfilled, (state, action) => {
			state.passwordResetStatus = 'set';
			const decoded = jwt_decode(action.payload.token);
			state.user = (decoded as any).user as User;
			state.loggedIn = 'valid';
			state.newPassword = '';
			state.confirmNewPassword = '';
			state.requireTermsOfService = false;
		});
		builder.addCase(requestNewPassword.pending, (state, action) => {
			state.passwordResetStatus = 'setting';
		});
		builder.addCase(requestNewPassword.rejected, (state, action) => {
			state.passwordResetStatus = 'error';
			state.passwordResetErrorMessage = action.error.message ?? '';
		});
		builder.addCase(fetchDomain.fulfilled, (state, action) => {
			state.domain = action.payload ?? '';
		});
		builder.addCase(fetchCommunities.fulfilled, (state, action) => {
			state.communities = action.payload ?? [];
		});
		builder.addCase(fetchImageToken.fulfilled, (state, action) => {
			state.imageToken = action.payload ?? '';
		});
		builder.addCase(fetchPlatform.fulfilled, (state, action) => {
			state.platform = action.payload ?? 'web';
		});
	}
});

// Action creators are generated for each case reducer function
export const {
	setEmail,
	setPassword,
	setNewPassword,
	setConfirmNewPassword,
	setLoggedIn,
	setLoginError,
	setLoginMessage,
	setPasswordResetErrorMessage,
	setPasswordResetStatus,
	setRequireTermsOfService,
	setEmailLogin,
	setDomain,
	setImageToken,
	setPlatform
} = loginSlice.actions;
export default loginSlice.reducer;
